<script>
   /**************************************************************/
   /* DataLoader for PCA/SIMCA app                               */
   /* -------------------------                                  */
   /* Loads test set from CSV file and returns is main output.   */
   /* If PCA model is provided it also applied the model and     */
   /* returns object with PCA results. In this case it will      */
   /* also recalculate the result object if array with outliers  */
   /* has been changed                                           */
   /**************************************************************/


   import { parseCSV, app_pca_predict, reverseColumns, parseCSVParams } from '../utils.js';

   import DatasetWidget from '../widgets/DatasetWidget.svelte';

   import FileSelector from '../controls/FileSelector.svelte';
   import Spinner from '../controls/Spinner.svelte';
   import Select from '../controls/Select.svelte';
   import Container from '../controls/Container.svelte';
   import Button from '../controls/Button.svelte';
   import TextInput from '../controls/TextInput.svelte';


   /*****************************************/
   /* Component parameters                  */
   /*****************************************/

   // inputs
   export let WASM = null;                      // WASM object to make predictions if model is available (optional)
   export let model = null;                     // object with PCA model (optional)
   export let outliers = [];                    // array with outlier indices (optional)

   export let className = "Class";              // name of class related fields in the widget (e.g. 'Class' or 'Group')
   export let classMandatory = false;           // logical, if class column is mandatory in the data file
   export let classUnique = false;              // logical, if class labels must be all the same

   // output
   export let dataset;                          // JSON with datasets
   export let res = null;                       // JSON with PCA results (if model is provided)


   /*****************************************/
   /* Component code                        */
   /*****************************************/

   let dataFile;                                         // CSV file
   let varvaluesName = '';                               // name of the variable values to be used as header
   let varvaluesUnits = '';                              // units of the variable values
   let outliersOld = outliers;                           // array to track changes in outliers
   let promise;                                          // variables for WASM promise

   // app status for handling and showing errors
   let status = 'neutral';
   let errMsg = '';

   // possible delimeters and header options
   const sepOptions = [',', ';'];
   let headerOptions = model ? ['no', 'yes'] : ['no', 'labels', 'values'];

   // these parameters will be autodetected from CSV file
   let sep;              // seperator between values
   let header;           // first row contains header (labels or values)
   let hasLabels;        // first column contains row labels
   let hasClassLabels;   // if data file contains class labels

   // method to apply PCA model
   function predict(model, dataset, outliers) {
      promise = WASM()
         .then(Module => {
            res = app_pca_predict(Module, model, dataset, outliers);
         })
         .catch(e =>{
            console.error(e);
            res = null;
            status = 'error';
            errMsg = e;
         });
   };


   function guessParams(dataFile, model) {
      const hasModel = model !== null && model !== undefined;
      if (!dataFile || dataFile.size < 10) return;
      const chunkSize = 100000;
      const first100KB = dataFile.slice(0, chunkSize);

      const reader = new FileReader();
      reader.onload = () => {
         const csvString = reader.result;
         if (!csvString || csvString.length < 0) return;
         let hasLabelsBool, hasClassLabelsBool;
         [sep, hasLabelsBool, hasClassLabelsBool, header] = parseCSVParams(csvString);
         hasLabels = hasLabelsBool ? 'yes' : 'no';
         hasClassLabels = (classMandatory || hasClassLabelsBool) ? 'yes' : 'no';
         if (hasModel && header !== 'no') header = 'yes';
      };

      reader.onerror = function(e) {
         csvString = null;
         status = 'error';
         errMsg = e;
         console.error('Error reading CSV file.', e);
      };

      reader.readAsText(first100KB);

   }

   // method for parsing CSV content provided as one large string to dataset object.
   function setData(strData) {
      try{
         const d = parseCSV(
            strData,
            sep,
            header === 'yes' ? 'labels' : header, // when load data for prediction simpler interface is used
            hasLabels === 'yes',
            hasClassLabels === 'yes',
            varvaluesName,
            varvaluesUnits
         );

         if (d.values.nrows < 1 ) {
            throw new Error('Dataset must have at least 1 row.');
         }

         if (model && d.values.ncols !== (model.ncols + model.exclvars.length)) {
            throw new Error('Dataset has different number of columns than the training set.');
         }

         if (d.classNames.length > d.values.nrows / 2) {
            throw new Error('Dataset has too many unique ' + className.toLowerCase() + ' labels (' + d.classNames.length + '), check your CSV file.');
         }

         if (classMandatory && d.classNames.length === 0) {
            throw new Error('Dataset set must have a column filled with class names.');
         }

         if (classUnique && d.classNames.length > 1) {
            throw new Error('Dataset set must have a column filled with only single (target) class name.');
         }

         d['fileName'] = dataFile.name.replace(/\.[^/.]+$/, "");

         // if model is available inherit variable properties from the model
         if (model) {
            // if data values in the training set were in reverse order it is expected
            // that they are in the reverse order in this dataset as well
            if (model.varrev) d.values = reverseColumns(d.values),
            d.varlabels = model.varlabels;
            d.varvalues = model.varvalues;
            d.varvaluesName = model.varvaluesName;
            d.varvaluesUnits = model.varvaluesUnits;
         }

         dataset = d;
         if (model) {
            // since the dataset is just loaded we do not expect any outliers
            predict(model, dataset, []);
         }

         outliersOld = [];
         errMsg = ''
         status = 'success';
      } catch (e) {
         dataset = null;
         outliersOld = [];
         status = 'error';
         errMsg = e;
      }
   }

   // promise for reading the CSV file
   function readFile (e) {
      const reader = new FileReader();
      reader.onload = () => { setData(reader.result) };
      reader.readAsText(dataFile);
   }

   // reset whole component to initial state
   function reset () {
      dataFile = null;
   }

   // reactive expression for tracking changes of 'dataFile'
   $: if (dataFile === null) {
      // if data file is empty reset status and error messages
      dataset = null;
   } else {
      // if not just remove error messages if any
      guessParams(dataFile, model)
      status = 'neutral';
      errMsg = '';
   }

   // reactive expression for tracking changes of 'sep'
   $: if (sep) {
      // if separator was changed remove error message
      status = 'neutral';
      errMsg = '';
   }

   // if array with outliers has been changed, make new predictions
   $: if (dataset && model && outliers !== outliersOld) {
      try{
         res = predict(model, dataset, outliers);
         outliersOld = outliers;
         errMsg = ''
         status = 'success';
      } catch (e) {
         dataset = null;
         status = 'error';
         errMsg = e;
      }
   }
</script>

{#if !dataset }

   <!-- show if data is not loaded -->
   <!-- reset component if dataFile is changed inside -->
   {#key dataFile === undefined}
   <Container>
      <FileSelector
         id="dataFile"
         message="Select CSV file with dataset"
         bind:file={dataFile}
      />
   </Container>
   {/key}

   <!-- show if file is selected but data is not loaded -->
   {#if dataFile}
   <Container label="Delimiter:">
      <Select id="sep" bind:value={sep} options={sepOptions} />
   </Container>
   <Container label="Row labels:">
      <Select id="hasLabels" bind:value={hasLabels} options={['no', 'yes']} />
   </Container>
   {#if !classMandatory}
   <Container label="{className} labels:">
      <Select id="hasLabels" bind:value={hasClassLabels} options={['no', 'yes']} />
   </Container>
   {/if}
   <Container label="Header:">
      <Select id="header" bind:value={header} options={headerOptions} />
   </Container>
   {#if header==='values'}
   <Container label="Header name:">
      <TextInput id="varvaluesName" bind:value={varvaluesName} placeholder="e.g. Wavelength" />
   </Container>
   <Container label="Header units:">
      <TextInput id="varvaluesUnits" bind:value={varvaluesUnits} maxLength={10} placeholder="e.g. nm" />
   </Container>
   {/if}
   <Container label="&nbsp;">
      <Button on:click={readFile}>Load</Button>
   </Container>
   {/if}

{:else}


{#if promise}

   <!-- calculation is in process -->
   {#await promise}
      <Container>
         <Spinner />
         <p>{model ? 'Loading data and apply model...' : 'Loading data...'}</p>
      </Container>

   {:then value}
      <!-- status if succeed -->
      {#if dataset !== null}
      <!-- dataset is loaded - show info and cancel button -->
      <DatasetWidget {dataset} on:click={reset} />
      {/if}

   {:catch error}
      <!-- error message if failed -->
      <Container status="error">
         <p>{error}</p>
      </Container>
   {/await}

{:else}
   {#if dataset !== null}
   <!-- dataset is loaded - show info and cancel button -->
   <DatasetWidget {dataset} on:click={reset} />
   {/if}
{/if}



{/if}

<!-- error message -->
{#if status === 'error' || errMsg}
<Container status="error">
   <p>{errMsg}</p>
</Container>
{/if}

